import { useState, type FormEvent, type ChangeEvent } from 'react';
import { useAuth } from '../context/AuthContext';

interface LoginProps {
  onSuccess?: () => void;
}

// Demo credential helpers expose account emails + passwords in the shipped JS
// bundle, so they are compiled out of production builds (dev/test only).
// The demo password itself is read from an env var (at render time) so no
// credential literal exists in this source file. Set VITE_DEMO_PASSWORD in
// .env.local for local development — see .env.example.
export function Login({ onSuccess }: LoginProps) {
  const showDemoHelpers = Boolean(import.meta.env?.DEV) &&
    Boolean(import.meta.env?.VITE_DEMO_PASSWORD);
  const demoEmail = 'admin@saindustries.com';
  const demoPassword = String(import.meta.env?.VITE_DEMO_PASSWORD || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { signIn } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await signIn(email, password);
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-sm w-full">
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="mx-auto h-12 w-12 rounded-lg bg-blue-600 flex items-center justify-center text-white text-lg font-bold mb-3">⚙</div>
            <h1 className="text-lg font-bold text-gray-900">SAI KRISHNA INDUSTRIES</h1>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mt-0.5">Quotation & Pricing Portal</p>
            <p className="text-xs text-gray-500 mt-2">Sign in to access quotation tools and customer database</p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs font-medium mb-4">
              {error}
            </div>
          )}

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Work Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                required
                placeholder="name@saindustries.com"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs font-semibold text-gray-700">Password</label>
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg disabled:opacity-50 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  'Sign In to Portal →'
                )}
              </button>
              {showDemoHelpers && (
                <button
                  type="button"
                  onClick={() => {
                    setEmail(demoEmail);
                    setPassword(demoPassword);
                  }}
                  className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                >
                  Auto-Fill Demo
                </button>
              )}
            </div>
          </form>

          {/* Quick Demo Switcher for All 6 Roles (dev/test builds with demo env configured) */}
          {showDemoHelpers && (
            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 text-center">
                ⚡ Quick Login by Role (Testing & Verification)
              </p>
              <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                <button
                  type="button"
                  onClick={() => { setEmail(demoEmail); setPassword(demoPassword); }}
                  className="p-1.5 text-left rounded bg-purple-50 hover:bg-purple-100 text-purple-800 font-medium border border-purple-200 transition-colors flex items-center justify-between"
                >
                  <span>🛡️ Admin</span>
                  <span className="text-[9px] text-purple-500">Full</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setEmail('soly@saikrishnaindustries.com'); setPassword(demoPassword); }}
                  className="p-1.5 text-left rounded bg-amber-50 hover:bg-amber-100 text-amber-800 font-medium border border-amber-200 transition-colors flex items-center justify-between"
                >
                  <span>👑 Executive</span>
                  <span className="text-[9px] text-amber-600">Director</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setEmail('manager@saindustries.com'); setPassword(demoPassword); }}
                  className="p-1.5 text-left rounded bg-blue-50 hover:bg-blue-100 text-blue-800 font-medium border border-blue-200 transition-colors flex items-center justify-between"
                >
                  <span>👔 Manager</span>
                  <span className="text-[9px] text-blue-500">Ops</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setEmail('salesrep@saindustries.com'); setPassword(demoPassword); }}
                  className="p-1.5 text-left rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-medium border border-emerald-200 transition-colors flex items-center justify-between"
                >
                  <span>💼 Sales Rep</span>
                  <span className="text-[9px] text-emerald-600">Quotes</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setEmail('accountant@saindustries.com'); setPassword(demoPassword); }}
                  className="p-1.5 text-left rounded bg-cyan-50 hover:bg-cyan-100 text-cyan-800 font-medium border border-cyan-200 transition-colors flex items-center justify-between"
                >
                  <span>💰 Accountant</span>
                  <span className="text-[9px] text-cyan-600">Billing</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setEmail('marketing@saindustries.com'); setPassword(demoPassword); }}
                  className="p-1.5 text-left rounded bg-pink-50 hover:bg-pink-100 text-pink-800 font-medium border border-pink-200 transition-colors flex items-center justify-between"
                >
                  <span>📣 Marketing</span>
                  <span className="text-[9px] text-pink-600">SEO/Ads</span>
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-gray-100 text-center text-xs text-gray-400">
            🔒 Protected by Enterprise Row-Level Security
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
